import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CompanionNotice } from '../lib/companion.js';

/** A thread whose reply finished and that nobody has opened since. Newest last. */
export interface DoneThread { conversationId: string; projectId: string; title: string }

const DONE_KEY = 'jabberr.doneThreads';

/** Refreshes and reloads keep the dots; they live in localStorage until each thread is opened. */
function loadDone(): DoneThread[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(DONE_KEY) ?? '[]');
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((t) =>
      t && typeof t === 'object' && 'conversationId' in t && 'projectId' in t && 'title' in t
        ? [{ conversationId: String(t.conversationId), projectId: String(t.projectId), title: String(t.title) }]
        : [],
    );
  } catch {
    return [];
  }
}

interface CompanionState {
  waiting: boolean;
  notice: CompanionNotice | null;
  done: DoneThread[];
  setWaiting: (waiting: boolean) => void;
  setNotice: (notice: CompanionNotice | null) => void;
  markDone: (thread: DoneThread) => void;
  clearDone: (conversationId: string) => void;
}

const CompanionContext = createContext<CompanionState | null>(null);

/** Jabber lives in the layout, so pages push their status here instead of rendering him. */
export function CompanionProvider({ children }: { children: ReactNode }) {
  const [waiting, setWaiting] = useState(false);
  const [notice, setNotice] = useState<CompanionNotice | null>(null);
  const [done, setDone] = useState<DoneThread[]>(loadDone);
  useEffect(() => {
    try { localStorage.setItem(DONE_KEY, JSON.stringify(done)); } catch { /* Still works without storage. */ }
  }, [done]);
  const markDone = useCallback((thread: DoneThread) => {
    setDone((prev) => [...prev.filter((t) => t.conversationId !== thread.conversationId), thread]);
  }, []);
  const clearDone = useCallback((conversationId: string) => {
    setDone((prev) => prev.filter((t) => t.conversationId !== conversationId));
  }, []);
  const value = useMemo(() => ({ waiting, notice, done, setWaiting, setNotice, markDone, clearDone }), [waiting, notice, done, setWaiting, setNotice, markDone, clearDone]);
  return <CompanionContext.Provider value={value}>{children}</CompanionContext.Provider>;
}

export function useCompanion(): CompanionState {
  const ctx = useContext(CompanionContext);
  if (!ctx) throw new Error('useCompanion outside CompanionProvider');
  return ctx;
}
